import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import ContentLoader from "../ContentLoader";
import PdfReportPreviewModal from "../PdfReportPreviewModal";
import RecordDetailModal from "../RecordDetailModal";
import TableSortSelect from "../TableSortSelect";
import { Button, Tabs } from "../ui";
import { confirmAlert, showAlert } from "../AppAlert/appAlert";
import { downloadReport, fetchReports } from "../../services/reportsApi";
import { addOrUpdateReport } from "../../services/smeApi";
import { canCustomizeSavedReport } from "./ReportCustomizeModal";
import { cn } from "../../utils/cn";
import { DEFAULT_TABLE_SORT, sortTableRows } from "../../utils/tableSort";

const normalizeReportStatus = (value: unknown) =>
  String(value || "submitted").trim().toLowerCase();

const reportStatusLabel = (value: unknown) => {
  const status = normalizeReportStatus(value);
  if (status === "edit") return "Edit";
  if (status === "upload") return "Upload";
  return "Submitted";
};

const reportStatusClassName = (value: unknown) => {
  const status = normalizeReportStatus(value);
  if (status === "submitted") return "app-status app-status--success";
  if (status === "edit") return "app-status app-status--info";
  if (status === "upload") return "app-status app-status--warning";
  return "app-status app-status--muted";
};

const isSavedReportStatus = (value: unknown) => {
  const status = normalizeReportStatus(value);
  return status === "edit" || status === "upload";
};

export type NormalizedProjectReport = {
  id: number | string | null;
  reportItemId: string;
  remarks: string;
  attachmentUrl: string;
  sourceHtmlUrl: string;
  status: string;
  reportDate: string | null;
  submittedAt: string | null;
};

const normalizeReport = (raw: Record<string, unknown>): NormalizedProjectReport => ({
  id: (raw?.id ?? raw?.Id ?? null) as number | string | null,
  reportItemId: String(raw?.reportItemId ?? raw?.ReportItemId ?? "").trim(),
  remarks: String(raw?.remarks ?? raw?.Remarks ?? "").trim(),
  attachmentUrl: String(raw?.attachmentUrl ?? raw?.AttachmentUrl ?? "").trim(),
  sourceHtmlUrl: String(raw?.sourceHtmlUrl ?? raw?.SourceHtmlUrl ?? "").trim(),
  status: String(raw?.status ?? raw?.Status ?? "submitted").trim() || "submitted",
  reportDate: (raw?.reportDate ?? raw?.ReportDate ?? null) as string | null,
  submittedAt: (raw?.submittedAt ?? raw?.SubmittedAt ?? null) as string | null,
});

const extractFileName = (filePath: string) => {
  if (!filePath) return "";
  const normalized = String(filePath).replace(/\\/g, "/");
  const parts = normalized.split("/");
  return parts[parts.length - 1] || "";
};

const formatReportDate = (dateValue: string | null | undefined) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    year: "numeric",
  };
  const datePart = new Intl.DateTimeFormat("en-US", options).format(date);
  const timePart = date.toLocaleTimeString("en-US");
  return `${datePart}, ${timePart}`;
};

const ReportEmptyCell = () => <span className="app-td__empty">—</span>;

export type SmeProjectReportHistoryProps = {
  projectId: number | string | null | undefined;
  clientUserId?: number | string | null;
  reloadToken?: number;
  onCustomizeReport?: (report: NormalizedProjectReport) => void;
  onReportsChanged?: () => void;
};

export default function SmeProjectReportHistory({
  projectId,
  clientUserId = null,
  reloadToken = 0,
  onCustomizeReport,
  onReportsChanged,
}: SmeProjectReportHistoryProps) {
  const [reports, setReports] = useState<NormalizedProjectReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(DEFAULT_TABLE_SORT);
  const [historyTab, setHistoryTab] = useState("saved");

  const [previewState, setPreviewState] = useState<{
    reportItemId: string;
    fileName: string;
  } | null>(null);
  const [viewReport, setViewReport] = useState<NormalizedProjectReport | null>(null);
  const [submittingReportItemId, setSubmittingReportItemId] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    const pid = projectId != null ? String(projectId).trim() : "";
    if (!pid) {
      setReports([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");
    try {
      const data = await fetchReports(pid);
      const list = Array.isArray(data) ? data : [];
      setReports(list.map((row) => normalizeReport(row as Record<string, unknown>)));
    } catch (error) {
      console.error("Error loading project reports:", error);
      setReports([]);
      setLoadError("Could not load project reports.");
    } finally {
      setLoading(false);
    }
  }, [projectId, reloadToken]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const savedReports = useMemo(
    () => reports.filter((report) => isSavedReportStatus(report.status)),
    [reports]
  );

  const submittedOnlyReports = useMemo(
    () => reports.filter((report) => normalizeReportStatus(report.status) === "submitted"),
    [reports]
  );

  const activeSource = historyTab === "saved" ? savedReports : submittedOnlyReports;
  const searchNeedle = searchTerm.trim().toLowerCase();

  const tableRows = useMemo(() => {
    const filtered = searchNeedle
      ? activeSource.filter((report) => {
          const haystack = [
            report.reportItemId,
            report.remarks,
            report.status,
            reportStatusLabel(report.status),
            extractFileName(report.attachmentUrl),
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(searchNeedle);
        })
      : activeSource;

    return sortTableRows(filtered, sortKey, {
      name: (report: NormalizedProjectReport) => report.reportItemId,
      date: (report: NormalizedProjectReport) => {
        const raw = report.submittedAt || report.reportDate;
        const ts = Date.parse(raw || "");
        return Number.isNaN(ts) ? 0 : ts;
      },
    });
  }, [activeSource, searchNeedle, sortKey]);

  const emptyMessage =
    historyTab === "saved"
      ? "No saved reports yet. Use Save report after generating or uploading a file."
      : "No submitted reports yet. Submit a report when it is ready for the client.";

  const hasAttachment = (report: NormalizedProjectReport) =>
    Boolean(report.attachmentUrl?.trim());

  const hasAnyReports = savedReports.length > 0 || submittedOnlyReports.length > 0;

  const handleSubmitSavedReport = async (report: NormalizedProjectReport) => {
    const pid = projectId != null ? String(projectId).trim() : "";
    const reportItemId = report.reportItemId?.trim();
    if (!pid || !reportItemId) {
      return;
    }
    if (!clientUserId) {
      showAlert({
        type: "warning",
        title: "Cannot submit",
        message: "Client user is missing for this project.",
        confirmText: "OK",
      });
      return;
    }
    if (!report.attachmentUrl?.trim()) {
      showAlert({
        type: "warning",
        title: "Cannot submit",
        message: "This saved report has no attachment file.",
        confirmText: "OK",
      });
      return;
    }

    const confirmed = await confirmAlert({
      type: "warning",
      title: "Submit report",
      message: `Submit "${reportItemId}" to the client? It will move to Submitted and appear in the client report list.`,
      confirmText: "Submit",
      cancelText: "Cancel",
    });
    if (!confirmed) {
      return;
    }

    setSubmittingReportItemId(reportItemId);
    try {
      await addOrUpdateReport(pid, clientUserId, report.remarks, [], {
        status: "submitted",
        copyFromReportItemId: reportItemId,
      });
      showAlert({
        type: "success",
        title: "Report submitted",
        message: "The report was submitted successfully and is now in the Submitted list.",
        confirmText: "Got it",
      });
      setHistoryTab("submitted");
      await loadReports();
      onReportsChanged?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to submit the saved report.";
      showAlert({
        type: "error",
        title: "Submit failed",
        message,
        confirmText: "OK",
      });
    } finally {
      setSubmittingReportItemId(null);
    }
  };

  const renderTable = () => {
    if (loading) {
      return <ContentLoader compact label="Loading project reports…" />;
    }

    if (loadError) {
      return <p className="sme-report-history__empty-hint m-0">{loadError}</p>;
    }

    if (activeSource.length === 0) {
      return (
        <div className="sme-report-history__empty">
          <p className="sme-report-history__empty-title m-0">No reports yet</p>
          <p className="sme-report-history__empty-hint m-0">{emptyMessage}</p>
        </div>
      );
    }

    if (tableRows.length === 0) {
      return (
        <div className="sme-report-history__empty">
          <p className="sme-report-history__empty-title m-0">No matches</p>
          <p className="sme-report-history__empty-hint m-0">
            No reports match &ldquo;{searchTerm.trim()}&rdquo;. Try another term or clear the search.
          </p>
        </div>
      );
    }

    const showSubmittedColumn = tableRows.some(
      (report) => normalizeReportStatus(report.status) === "submitted"
    );

    const isSubmittedTab = historyTab === "submitted";

    return (
      <div
        className={cn(
          "app-table-wrap app-table-wrap--refined sme-report-history-table dash-table-mobile-skip",
          isSubmittedTab && "sme-report-history-table--submitted"
        )}
      >
        <table className="table table-striped">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Report ID</th>
              <th scope="col">Status</th>
              <th scope="col">Remarks</th>
              <th scope="col">Created</th>
              {showSubmittedColumn ? <th scope="col">Submitted</th> : null}
              <th scope="col" className="sme-report-history__file-col">
                File
              </th>
              <th scope="col" className="sme-report-history__actions-col">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((report, index) => {
              const fileName = extractFileName(report.attachmentUrl) || "report.pdf";
              const isPdf = /\.pdf$/i.test(fileName);
              const remarks = report.remarks?.trim();

              return (
                <tr
                  key={String(report.id ?? report.reportItemId ?? index)}
                  className="rdm-row-clickable"
                  onClick={() => setViewReport(report)}
                >
                  <td>
                    <span className="app-td__index">{index + 1}</span>
                  </td>
                  <td title={report.reportItemId || undefined}>
                    <span className="app-td__title">
                      {report.reportItemId?.trim() || "—"}
                    </span>
                  </td>
                  <td>
                    <span className={reportStatusClassName(report.status)}>
                      {reportStatusLabel(report.status)}
                    </span>
                  </td>
                  <td>
                    {remarks ? (
                      <span className="app-td__desc sme-report-history__remarks">
                        {remarks}
                      </span>
                    ) : (
                      <ReportEmptyCell />
                    )}
                  </td>
                  <td>
                    <span className="app-td__meta">
                      {formatReportDate(report.reportDate)}
                    </span>
                  </td>
                  {showSubmittedColumn ? (
                    <td>
                      {normalizeReportStatus(report.status) === "submitted" ? (
                        <span className="app-td__meta">
                          {formatReportDate(report.submittedAt)}
                        </span>
                      ) : (
                        <ReportEmptyCell />
                      )}
                    </td>
                  ) : null}
                  <td className="sme-report-history__file-col">
                    {hasAttachment(report) ? (
                      <span
                        className="app-td__meta sme-report-history__file-name"
                        title={fileName}
                      >
                        {fileName}
                      </span>
                    ) : (
                      <span className="app-td__desc sme-report-history__no-file">
                        No file
                      </span>
                    )}
                  </td>
                  <td
                    className="sme-report-history__actions sme-report-history__actions-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {hasAttachment(report) ? (
                      isSubmittedTab ? (
                        <div className="btn-list-actions sme-report-history__action-group sme-report-history__action-group--submitted">
                          {isPdf ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              className="sme-report-history__action-btn"
                              onClick={() =>
                                setPreviewState({
                                  reportItemId: report.reportItemId,
                                  fileName,
                                })
                              }
                            >
                              Preview
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="sme-report-history__action-btn"
                            onClick={() =>
                              downloadReport(projectId, report.reportItemId, fileName)
                            }
                          >
                            Download
                          </Button>
                        </div>
                      ) : (
                        <div className="btn-list-actions sme-report-history__action-group sme-report-history__action-group--saved">
                          <div className="sme-report-history__action-slot sme-report-history__action-slot--customize">
                            {canCustomizeSavedReport(report) ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="sme-report-history__action-btn"
                                onClick={() => onCustomizeReport?.(report)}
                              >
                                Customize
                              </Button>
                            ) : (
                              <span
                                className="sme-report-history__action-spacer"
                                aria-hidden
                              />
                            )}
                          </div>
                          <div className="sme-report-history__action-slot sme-report-history__action-slot--preview">
                            {isPdf ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="primary"
                                className="sme-report-history__action-btn"
                                onClick={() =>
                                  setPreviewState({
                                    reportItemId: report.reportItemId,
                                    fileName,
                                  })
                                }
                              >
                                Preview
                              </Button>
                            ) : (
                              <span
                                className="sme-report-history__action-spacer"
                                aria-hidden
                              />
                            )}
                          </div>
                          <div className="sme-report-history__action-slot sme-report-history__action-slot--download">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="sme-report-history__action-btn"
                              onClick={() =>
                                downloadReport(projectId, report.reportItemId, fileName)
                              }
                            >
                              Download
                            </Button>
                          </div>
                          {isSavedReportStatus(report.status) ? (
                            <div className="sme-report-history__action-slot sme-report-history__action-slot--submit">
                              <Button
                                type="button"
                                size="sm"
                                variant="primary"
                                className="sme-report-history__action-btn"
                                isLoading={submittingReportItemId === report.reportItemId}
                                disabled={Boolean(submittingReportItemId)}
                                onClick={() => void handleSubmitSavedReport(report)}
                              >
                                Submit
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      )
                    ) : (
                      <span className="app-td__desc sme-report-history__no-file">
                        No attachment
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div className="sme-tracking-report-card sme-report-history">
        <div className="sme-tracking-report-card__header sme-report-history__header">
          <div className="sme-report-history__intro">
            <h3 className="sme-tracking-report-card__title app-heading-accent">
              Project reports
            </h3>
            <p className="sme-report-history__lede m-0">
              Saved drafts and submitted versions for this project. Open a row for full details.
            </p>
          </div>
          {hasAnyReports ? (
            <div className="dash-list-toolbar__actions sme-report-history__toolbar">
              <div className="dash-search-field dash-table-search sme-report-history__search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="search"
                  autoComplete="off"
                  className="dash-search-field__input"
                  placeholder="Search ID, remarks, or file…"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  aria-label="Search project reports"
                />
              </div>
              <TableSortSelect value={sortKey} onChange={setSortKey} />
            </div>
          ) : null}
        </div>
        <div className="sme-tracking-report-card__body sme-report-history__body">
          <Tabs
            variant="segmented"
            className="sme-tracking-report-history-tabs sme-tracking-report-history-tabs--table-outside"
            value={historyTab}
            onChange={setHistoryTab}
            tabs={[
              {
                id: "saved",
                label: `Saved (${savedReports.length})`,
                content: null,
              },
              {
                id: "submitted",
                label: `Submitted (${submittedOnlyReports.length})`,
                content: null,
              },
            ]}
          />
          <div className="sme-report-history__table-panel">{renderTable()}</div>
        </div>
      </div>

      <RecordDetailModal
        show={Boolean(viewReport)}
        onHide={() => setViewReport(null)}
        title="Report Details"
        subtitle={
          viewReport?.submittedAt
            ? `Submitted ${formatReportDate(viewReport.submittedAt)}`
            : viewReport?.reportDate
              ? `Created ${formatReportDate(viewReport.reportDate)}`
              : undefined
        }
        fields={
          viewReport
            ? [
                { label: "Report ID", value: viewReport.reportItemId?.trim() || "—" },
                { label: "Created", value: formatReportDate(viewReport.reportDate) },
                { label: "Submitted", value: formatReportDate(viewReport.submittedAt) },
                { label: "Status", value: reportStatusLabel(viewReport.status) },
                {
                  label: "Remarks",
                  value: viewReport.remarks?.trim() || "No remarks",
                  fullWidth: true,
                },
                {
                  label: "Attachment",
                  value: hasAttachment(viewReport)
                    ? extractFileName(viewReport.attachmentUrl)
                    : "No attachment",
                },
              ]
            : []
        }
        actions={
          viewReport && hasAttachment(viewReport)
            ? [
                ...(/\.pdf$/i.test(extractFileName(viewReport.attachmentUrl))
                  ? [
                      {
                        type: "preview" as const,
                        onClick: () => {
                          const report = viewReport;
                          setViewReport(null);
                          if (report) {
                            setPreviewState({
                              reportItemId: report.reportItemId,
                              fileName: extractFileName(report.attachmentUrl) || "report.pdf",
                            });
                          }
                        },
                      },
                    ]
                  : []),
                {
                  type: "download" as const,
                  onClick: () => {
                    const report = viewReport;
                    setViewReport(null);
                    if (report) {
                      void downloadReport(
                        projectId,
                        report.reportItemId,
                        extractFileName(report.attachmentUrl) || "report.pdf"
                      );
                    }
                  },
                },
              ]
            : []
        }
      />

      <PdfReportPreviewModal
        show={Boolean(previewState)}
        onHide={() => setPreviewState(null)}
        title="Capability Assessment Report"
        projectId={projectId ?? undefined}
        reportItemId={previewState?.reportItemId}
        fileName={previewState?.fileName || "report.pdf"}
        onDownload={
          previewState
            ? () =>
                downloadReport(
                  projectId,
                  previewState.reportItemId,
                  previewState.fileName
                )
            : undefined
        }
      />
    </>
  );
}
